'use client';
import React, { useState, useRef } from 'react';
import { CheckCircle2, Circle, Mail, IdCard, ShieldCheck, Upload, X, AlertCircle, Loader2, ChevronRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface VerificationStep {
  id: number;
  title: string;
  description: string;
  icon: React.ElementType;
}

const STEPS: VerificationStep[] = [
  { id: 1, title: 'Email Confirmation', description: 'Verify your email address', icon: Mail },
  { id: 2, title: 'ID Document Upload', description: 'Upload a government-issued ID', icon: IdCard },
  { id: 3, title: 'Anti-Fraud Review', description: 'Final security check', icon: ShieldCheck },
];

const ID_TYPES = [
  { value: 'national_id', label: 'National ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'driving_license', label: "Driver\'s License" },
  { value: 'residence_permit', label: 'Residence Permit' },
];

interface VerificationData {
  emailConfirmed: boolean;
  idDocumentType: string;
  idDocumentUrl: string;
  idDocumentBackUrl: string;
  overallStatus: string;
  antiFraudStatus: string;
}

interface Props {
  initialData: VerificationData | null;
  onVerified: () => void;
}

export default function SellerVerificationStepper({ initialData, onVerified }: Props) {
  const { user } = useAuth();
  const supabase = createClient();

  const emailConfirmed = initialData?.emailConfirmed || user?.email_confirmed_at != null;
  const hasIdDoc = !!(initialData?.idDocumentUrl);
  const antiFraudStatus = initialData?.antiFraudStatus || 'pending';
  const overallStatus = initialData?.overallStatus || 'pending';

  const getActiveStep = () => {
    if (!emailConfirmed) return 1;
    if (!hasIdDoc) return 2;
    return 3;
  };

  const [activeStep, setActiveStep] = useState(getActiveStep());
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [selectedIdType, setSelectedIdType] = useState(initialData?.idDocumentType || '');
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [frontPreview, setFrontPreview] = useState<string>('');
  const [backPreview, setBackPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [idSubmitted, setIdSubmitted] = useState(hasIdDoc);
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);

  const stepStatus = (stepId: number) => {
    if (stepId === 1) return emailConfirmed ? 'done' : activeStep === 1 ? 'active' : 'pending';
    if (stepId === 2) return idSubmitted ? 'done' : activeStep === 2 ? 'active' : emailConfirmed ? 'active' : 'pending';
    if (stepId === 3) {
      if (overallStatus === 'approved') return 'done';
      if (antiFraudStatus === 'rejected') return 'rejected';
      return activeStep === 3 ? 'active' : 'pending';
    }
    return 'pending';
  };

  const handleResendEmail = async () => {
    if (!user?.email) return;
    setSendingEmail(true);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: user.email });
      if (error) throw error;
      setEmailSent(true);
      toast.success('Verification email sent! Check your inbox.');
    } catch {
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCheckEmailVerification = async () => {
    try {
      const { data: { user: freshUser } } = await supabase.auth.getUser();
      if (freshUser?.email_confirmed_at) {
        // Update verification record
        await supabase.from('seller_verifications').upsert({
          user_id: user?.id,
          email_confirmed: true,
          email_confirmed_at: new Date().toISOString(),
          overall_status: 'pending',
        }, { onConflict: 'user_id' });
        toast.success('Email verified successfully!');
        setActiveStep(2);
      } else {
        toast.error('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch {
      toast.error('Could not check verification status. Please try again.');
    }
  };

  const handleFileSelect = (side: 'front' | 'back', file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }
    const preview = URL.createObjectURL(file);
    if (side === 'front') {
      setFrontFile(file);
      setFrontPreview(preview);
    } else {
      setBackFile(file);
      setBackPreview(preview);
    }
  };

  const uploadFile = async (file: File, path: string): Promise<string> => {
    const { error } = await supabase.storage.from('id-documents').upload(path, file, { upsert: true });
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('id-documents').getPublicUrl(path);
    return publicUrl;
  };

  const handleIdSubmit = async () => {
    if (!selectedIdType) { toast.error('Please select an ID document type'); return; }
    if (!frontFile) { toast.error('Please upload the front side of your ID'); return; }
    setUploading(true);
    try {
      const frontPath = `${user?.id}/front_${Date.now()}_${frontFile.name}`;
      const frontUrl = await uploadFile(frontFile, frontPath);
      let backUrl = '';
      if (backFile) {
        const backPath = `${user?.id}/back_${Date.now()}_${backFile.name}`;
        backUrl = await uploadFile(backFile, backPath);
      }
      await supabase.from('seller_verifications').upsert({
        user_id: user?.id,
        email_confirmed: true,
        id_document_type: selectedIdType,
        id_document_url: frontUrl,
        id_document_back_url: backUrl || null,
        id_document_submitted_at: new Date().toISOString(),
        anti_fraud_status: 'submitted',
        overall_status: 'submitted',
      }, { onConflict: 'user_id' });
      setIdSubmitted(true);
      setActiveStep(3);
      toast.success('ID document uploaded successfully!');
    } catch {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (overallStatus === 'approved') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle2 size={40} className="text-green-500" />
        </div>
        <h2 className="text-2xl font-700 text-gray-900 mb-2">Verification Complete!</h2>
        <p className="text-gray-500 mb-6 max-w-sm">Your seller account has been verified. You can now list properties on Directly.</p>
        <button
          onClick={onVerified}
          className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-600 rounded-xl transition-all duration-150 flex items-center gap-2"
        >
          Go to Property Management <ChevronRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Stepper Header */}
      <div className="flex items-center justify-between mb-10">
        {STEPS.map((step, idx) => {
          const status = stepStatus(step.id);
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center gap-2 flex-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  status === 'done' ? 'bg-green-500 border-green-500' :
                  status === 'active' ? 'bg-primary-500 border-primary-500' :
                  status === 'rejected'? 'bg-red-500 border-red-500' : 'bg-white border-gray-200'
                }`}>
                  {status === 'done' ? (
                    <CheckCircle2 size={22} className="text-white" />
                  ) : status === 'rejected' ? (
                    <X size={22} className="text-white" />
                  ) : (
                    <step.icon size={20} className={status === 'active' ? 'text-white' : 'text-gray-400'} />
                  )}
                </div>
                <div className="text-center">
                  <p className={`text-xs font-600 ${status === 'active' ? 'text-primary-600' : status === 'done' ? 'text-green-600' : 'text-gray-400'}`}>
                    {step.title}
                  </p>
                </div>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-6 transition-all duration-300 ${
                  stepStatus(STEPS[idx + 1].id) !== 'pending' || stepStatus(step.id) === 'done' ? 'bg-primary-300' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">

        {/* Step 1: Email Confirmation */}
        {activeStep === 1 && !emailConfirmed && (
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail size={28} className="text-blue-500" />
            </div>
            <h3 className="text-xl font-700 text-gray-900 mb-2">Confirm Your Email</h3>
            <p className="text-gray-500 mb-2 text-sm">
              We sent a verification link to <span className="font-600 text-gray-700">{user?.email}</span>
            </p>
            <p className="text-gray-400 text-xs mb-6">Click the link in the email to verify your address, then come back here.</p>

            {emailSent && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4 text-sm text-green-700">
                <CheckCircle2 size={16} />
                Verification email sent! Check your inbox.
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleResendEmail}
                disabled={sendingEmail}
                className="px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-600 rounded-xl hover:bg-gray-50 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sendingEmail ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                Resend Email
              </button>
              <button
                onClick={handleCheckEmailVerification}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-600 rounded-xl transition-all duration-150 flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={15} />
                I've Verified My Email
              </button>
            </div>
          </div>
        )}

        {/* Step 2: ID Document Upload */}
        {(activeStep === 2 || (emailConfirmed && !idSubmitted)) && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <IdCard size={24} className="text-amber-500" />
              </div>
              <div>
                <h3 className="text-xl font-700 text-gray-900">Upload ID Document</h3>
                <p className="text-sm text-gray-500">Government-issued photo ID required</p>
              </div>
            </div>

            {/* ID Type Selector */}
            <div className="mb-5">
              <label className="block text-sm font-600 text-gray-700 mb-2">Document Type</label>
              <div className="grid grid-cols-2 gap-2">
                {ID_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setSelectedIdType(type.value)}
                    className={`px-4 py-2.5 rounded-xl border-2 text-sm font-500 transition-all duration-150 text-left ${
                      selectedIdType === type.value
                        ? 'border-primary-500 bg-primary-50 text-primary-700' :'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Front Upload */}
            <div className="mb-4">
              <label className="block text-sm font-600 text-gray-700 mb-2">Front Side <span className="text-red-500">*</span></label>
              <input ref={frontInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                onChange={e => e.target.files?.[0] && handleFileSelect('front', e.target.files[0])} />
              {frontPreview ? (
                <div className="relative rounded-xl overflow-hidden border border-gray-200 h-36">
                  <img src={frontPreview} alt="Front of ID document" className="w-full h-full object-cover" />
                  <button onClick={() => { setFrontFile(null); setFrontPreview(''); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ) : (
                <button onClick={() => frontInputRef.current?.click()}
                  className="w-full h-36 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary-300 hover:bg-primary-50 transition-all duration-150">
                  <Upload size={24} className="text-gray-400" />
                  <span className="text-sm text-gray-500">Click to upload front side</span>
                  <span className="text-xs text-gray-400">JPG, PNG, PDF — max 5MB</span>
                </button>
              )}
            </div>

            {/* Back Upload */}
            <div className="mb-6">
              <label className="block text-sm font-600 text-gray-700 mb-2">Back Side <span className="text-gray-400 font-400">(optional)</span></label>
              <input ref={backInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                onChange={e => e.target.files?.[0] && handleFileSelect('back', e.target.files[0])} />
              {backPreview ? (
                <div className="relative rounded-xl overflow-hidden border border-gray-200 h-36">
                  <img src={backPreview} alt="Back of ID document" className="w-full h-full object-cover" />
                  <button onClick={() => { setBackFile(null); setBackPreview(''); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ) : (
                <button onClick={() => backInputRef.current?.click()}
                  className="w-full h-28 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary-300 hover:bg-primary-50 transition-all duration-150">
                  <Upload size={20} className="text-gray-400" />
                  <span className="text-sm text-gray-500">Click to upload back side</span>
                </button>
              )}
            </div>

            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-xs text-amber-700">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
              <span>Your ID is encrypted and stored securely. It will only be used for identity verification and will not be shared with third parties.</span>
            </div>

            <button
              onClick={handleIdSubmit}
              disabled={uploading || !selectedIdType || !frontFile}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-600 rounded-xl transition-all duration-150 flex items-center justify-center gap-2"
            >
              {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading...</> : <><Upload size={16} /> Submit ID Document</>}
            </button>
          </div>
        )}

        {/* Step 3: Anti-Fraud Review */}
        {(activeStep === 3 || idSubmitted) && (
          <div className="text-center">
            {antiFraudStatus === 'rejected' || overallStatus === 'rejected' ? (
              <>
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X size={28} className="text-red-500" />
                </div>
                <h3 className="text-xl font-700 text-gray-900 mb-2">Verification Rejected</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Unfortunately, your verification was not approved. Please contact support for assistance.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                  {initialData?.antiFraudStatus === 'rejected' ? 'Your documents could not be verified. Please ensure you submitted a valid, unexpired government-issued ID.' : 'Verification failed. Please contact support.'}
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck size={28} className="text-purple-500" />
                </div>
                <h3 className="text-xl font-700 text-gray-900 mb-2">Under Review</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Your documents have been submitted and are being reviewed by our security team. This typically takes <span className="font-600 text-gray-700">1–2 business days</span>.
                </p>

                <div className="space-y-3 text-left mb-6">
                  {[
                    { label: 'Email Confirmed', done: true },
                    { label: 'ID Document Submitted', done: true },
                    { label: 'Anti-Fraud Check', done: false, pending: true },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                      {item.done ? (
                        <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                      ) : item.pending ? (
                        <Loader2 size={18} className="text-amber-500 animate-spin flex-shrink-0" />
                      ) : (
                        <Circle size={18} className="text-gray-300 flex-shrink-0" />
                      )}
                      <span className={`text-sm font-500 ${item.done ? 'text-gray-700' : 'text-gray-500'}`}>{item.label}</span>
                      {item.pending && <span className="ml-auto text-xs text-amber-600 font-500">In Progress</span>}
                      {item.done && <span className="ml-auto text-xs text-green-600 font-500">Complete</span>}
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-400">
                  You'll receive an email notification once your account is approved.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
